import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import type {Feature} from '@/store/onboardingSlices'
import {setFeatures} from '@/store/onboardingSlices'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import {getAdoBoardName} from '@/components/SelfOnboarding/util/getLabel'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {FEATURE_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {setLoading} from '@/store/slices/team/teamSlice'
import {Divider, Feedback, Label} from 'pepsico-ds'
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'
import type {RootState} from '@/store'
import CustomInput from '@/components/common/CustomInputWrapper'

const initialFeature: Feature = {
  teamName: '',
  onTimeDelivery: '',
  commitmentMet: '',
  velocity: '',
  defectDensity: '',
  automationEfficiency: '',
  testAutomation: '',
  overallAutomationCoverage: '',
  programTags: '',
  reusabilityTags: '',
  reusabilityBaselinePeriod: '',
  reusableStoryPoints: '',
  totalStoryPoints: '',
  automatedBaselinePeriod: '',
  automatedTestCases: '',
  totalTestCases: '',
  automationCandidateTestCases: '',
  organization: '',
  projectName: '',
}

type Suggestion = {id: string; label: string; fullObject: any}

const FeatureWidget = ({onValidationChange}: {onValidationChange?: (hasError: boolean) => void}) => {
  const feature = useSelector((state: any) =>
    Array.isArray(state.feature) && state.feature.length > 0 ? state.feature[0] : {...initialFeature},
  )
  const dispatch = useDispatch()
  useEffect(() => {
    if (!feature) {
      dispatch(setFeatures([{...initialFeature}]))
    }
  }, [feature, dispatch])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [touched, setTouched] = useState<{[idx: number]: {[field: string]: boolean | undefined}}>({})

  const updateFeature = (field: string, value: string) => {
    const newFeature = {...feature, [field]: value}
    dispatch(setFeatures([newFeature]))
  }
  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      0: {
        ...(prev[0] || {}),
        [field]: true,
      },
    }))
  }

  // Compute widgetTouched for field-level error display
  const widgetTouched = anyWidgetFieldTouched(touched)

  useEffect(() => {
    const hasError = widgetTouched && hasEmptyRequiredFields(['teamName'], [feature])
    if (onValidationChange) onValidationChange(hasError)
  }, [feature, touched, onValidationChange])

  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  return (
    <CardWrapper
      cardHeader={{
        rightAccessory: {
          iconButtons: [
            {
              icon: 'delete_outline',
              onClick: () => {
                dispatch(setFeatures([{...initialFeature}]))
                setTouched({})
              },
            },
          ],
        },
      }}
      cardBody={{type: 'slot', slot: <></>}}
      state="non-interactive"
      type="basic"
    >
      <form className="flex w-full flex-col items-start gap-8">
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-start gap-4">
            <div className="flex w-[282px] flex-col items-start">
              <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                <Label className={sharedStyles.autocompleteLabelFont} label={FEATURE_WIDGET.adoBoardName} />
                <AutoCompleteWrapper
                  icon="keyboard_arrow_down"
                  limitSuggestions={100}
                  onClear={() => updateFeature('teamName', '')}
                  placeholderText="Search ADO Board"
                  suggestions={suggestions}
                  isAutoComplete={true}
                  isLeadingIcon={false}
                  searchTerm={
                    typeof feature.teamName === 'string'
                      ? feature.teamName
                      : feature.teamName && typeof feature.teamName === 'object'
                        ? 'description' in feature.teamName
                          ? feature.teamName.description
                          : 'label' in feature.teamName
                            ? feature.teamName.label
                            : ''
                        : ''
                  }
                  onUpdate={async (query: string) => {
                    setLoading(true)
                    try {
                      const {getItemsByTypeBySearchField} = await import('@/services/team/teamService')
                      const results = await getItemsByTypeBySearchField('AgileTool', {
                        search: query,
                        searchField: 'options.teamName',
                      })
                      const fullObjMap: Record<string, any> = {}
                      const mapped = Array.isArray(results)
                        ? results.map((item: any) => {
                            const id = item.id || item._id || item.value || item.label
                            fullObjMap[id] = item
                            return {
                              id,
                              label: getAdoBoardName(item),
                              fullObject: item,
                            }
                          })
                        : []
                      setSuggestions(mapped)
                      setLoading(false)
                    } catch (err) {
                      setLoading(false)
                    }
                  }}
                  onSelectOption={(option: any) => updateFeature('teamName', option)}
                  onBlur={() => handleBlur('teamName')}
                />
                <Feedback
                  variant="info"
                  className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                  message={FEATURE_WIDGET.adoBoardNameHelperText}
                />
                {touched[0]?.['teamName'] && !feature.teamName && (
                  <div className="mt-1 text-xs text-red-500">Required</div>
                )}
              </div>
            </div>

            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="onTimeDelivery"
                label={FEATURE_WIDGET.onTimeDelivery}
                type="number"
                value={feature.onTimeDelivery}
                placeholder="E.g 50"
                onChange={e => updateFeature('onTimeDelivery', e.target.value)}
                helperText={FEATURE_WIDGET.onTimeDeliveryHelperText}
              />
            </div>
          </div>
          <div className="flex w-full items-start gap-4">
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="commitmentMet"
                label="Commitment Met (%)"
                type="number"
                value={feature.commitmentMet}
                placeholder="E.g 50"
                onChange={e => updateFeature('commitmentMet', e.target.value)}
                helperText={FEATURE_WIDGET.commitmentMetHelperText}
              />
            </div>
            {/* Velocity (Story Points) */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="velocity"
                label="Velocity (Story Points)"
                type="number"
                value={feature.velocity}
                placeholder="E.g 50"
                onChange={e => updateFeature('velocity', e.target.value)}
                helperText={FEATURE_WIDGET.velocityHelperText}
              />
            </div>
          </div>
          <div className="flex w-full items-start gap-4">
            {/* Defect Density */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="defectDensity"
                label="Defect Density"
                type="number"
                value={feature.defectDensity}
                placeholder="E.g 9"
                onChange={e => updateFeature('defectDensity', e.target.value)}
                helperText={FEATURE_WIDGET.defectDensityHelperText}
              />
            </div>
            {/* Automation Efficiency (%) */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="automationEfficiency"
                label="Automation Efficiency (%)"
                type="number"
                value={feature.automationEfficiency}
                placeholder="E.g 50"
                onChange={e => updateFeature('automationEfficiency', e.target.value)}
                helperText={FEATURE_WIDGET.automationTestHelperText}
              />
            </div>
          </div>
          <div className="flex w-full items-start gap-4">
            {/* Automation Coverage Target */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="testAutomation"
                label="Automation Coverage Target"
                type="number"
                value={feature.testAutomation}
                placeholder="Enter percentage"
                onChange={e => updateFeature('testAutomation', e.target.value)}
                helperText={FEATURE_WIDGET.automationCoverageTargetHelperText}
              />
            </div>
            {/* Overall Automation Coverage Target */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="overallAutomationCoverage"
                label="Overall Automation Coverage Target"
                type="number"
                value={feature.overallAutomationCoverage}
                placeholder="Enter percentage"
                onChange={e => updateFeature('overallAutomationCoverage', e.target.value)}
                helperText={FEATURE_WIDGET.overallAutomationCoverageTargetHelperText}
              />
            </div>
          </div>
          <div className="flex w-full items-start gap-4">
            {/* Program Tags */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name={`programTags`}
                label="Program Tags"
                type="text"
                value={feature.programTags}
                placeholder="E.g Global; Program"
                onChange={e => updateFeature('programTags', e.target.value)}
                helperText={FEATURE_WIDGET.programTagsHelperText}
              />
            </div>
          </div>
          <div className="flex w-[282px] flex-col items-start">
            <CustomInput
              inputSize="medium"
              name={`reusabilityTags`}
              label="Reusability Tags"
              type="text"
              value={feature.reusabilityTags}
              placeholder="E.g Global; Reusability"
              onChange={e => updateFeature('reusabilityTags', e.target.value)}
              helperText={FEATURE_WIDGET.reusabilityTagsHelperText}
            />
          </div>
        </div>

        <Divider />
        <div className="flex w-full flex-col items-start gap-6">
          <div className="font-inter text-sm leading-5 font-medium text-[var(--color-text-secondary)]">
            Base configuration for Reusability
          </div>
          <div className="flex w-full items-start gap-4">
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="reusabilityBaselinePeriod"
                label="Reusability Baseline Period"
                type="text"
                value={feature.reusabilityBaselinePeriod}
                placeholder="YYYY-MM (E.g 2024-07)"
                onChange={e => updateFeature('reusabilityBaselinePeriod', e.target.value)}
                helperText={FEATURE_WIDGET.reusabilityBaselineHelperText}
              />
            </div>

            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="reusableStoryPoints"
                label="Global Story Points"
                type="number"
                value={feature.reusableStoryPoints}
                placeholder="E.g 15"
                onChange={e => updateFeature('reusableStoryPoints', e.target.value)}
                helperText={FEATURE_WIDGET.globalStoryPointHelperText}
              />
            </div>
          </div>
          {/* Total Story Points */}
          <div className="flex w-[282px] flex-col items-start">
            <CustomInput
              inputSize="medium"
              name="totalStoryPoints"
              label="Total Story Points"
              type="number"
              value={feature.totalStoryPoints}
              placeholder="E.g 20"
              onChange={e => updateFeature('totalStoryPoints', e.target.value)}
              helperText={FEATURE_WIDGET.totalStoryPointHelperText}
            />
          </div>
          {/* </div> */}
        </div>

        <Divider />
        <div className="flex w-full flex-col items-start gap-6">
          <div className="font-inter text-sm leading-5 font-medium text-[var(--color-text-secondary)]">
            Baseline configuration for Test Automation
          </div>
          <div className="flex w-full items-start gap-4">
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="automatedBaselinePeriod"
                label="Baseline Period"
                type="text"
                value={feature.automatedBaselinePeriod}
                placeholder="YYYY-MM (e.g., 2024-12)"
                onChange={e => updateFeature('automatedBaselinePeriod', e.target.value)}
                helperText={FEATURE_WIDGET.baselineHelperText}
              />
            </div>
            {/* Automated Test Cases */}
            <div className="flex w-[282px] flex-col items-start">
              <CustomInput
                inputSize="medium"
                name="automatedTestCases"
                label="Automated Test Cases"
                type="number"
                value={feature.automatedTestCases}
                placeholder="Number of Automated Test Cases (e.g., 150)"
                onChange={e => updateFeature('automatedTestCases', e.target.value)}
                helperText={FEATURE_WIDGET.automationTestHelperText}
              />
            </div>
          </div>
          {/* Total Test Cases */}
          <div className="flex w-[282px] flex-col items-start">
            <CustomInput
              inputSize="medium"
              name="totalTestCases"
              label="Total Test Cases"
              type="number"
              value={feature.totalTestCases}
              placeholder="Total Number of Test Cases (e.g., 200)"
              onChange={e => updateFeature('totalTestCases', e.target.value)}
              helperText={FEATURE_WIDGET.totalTestCasesHelperText}
            />
          </div>
        </div>
      </form>
    </CardWrapper>
  )
}

export default FeatureWidget
